import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { liveMarketApi } from "src/api/liveMarketApi";
import clientInstance from "src/api/clientInstance";
import { getEventsByLeagueId, getGameBusinessMenuList, getLeaguesBySportId } from "src/api/commonApi";
import { APP_ORIGIN } from "src/config/constants";
import { isRacing } from "src/utils/functions";
import instance from "../../../api/instance";
import { RootState } from "../../../redux/store";
import { useSyncGlobalPageLoader } from "../../../contexts/global-page-loader-context";

interface IClientOption {
  user_id: string;
  user_name: string;
  display_name: string;
}

interface ICasinoRow {
  id: string;
  eventName: string;
  groupName: string;
}

interface IMarketNode {
  marketId: string;
  marketName: string;
}

interface IMarketTypeNode {
  marketType: string;
  markets: IMarketNode[];
}

interface IEventNode {
  eventId: string;
  eventName: string;
  marketTypes?: IMarketTypeNode[];
  isLoadingMarkets?: boolean;
  marketsLoaded?: boolean;
}

interface ILeagueNode {
  leagueId: string;
  leagueName: string;
  events?: IEventNode[];
  isLoadingEvents?: boolean;
  eventsLoaded?: boolean;
}

interface ISportNode {
  sportId: string;
  sportName: string;
  gameId: string;
  leagues?: ILeagueNode[];
  isLoadingLeagues?: boolean;
  leaguesLoaded?: boolean;
}

const CLIENT_SEARCH_MIN_LENGTH = 2;
const USERLOCK_ORIGIN = process.env.REACT_APP_FE_ORIGIN || APP_ORIGIN;
const LIVE_MARKET_GROUP_ID = process.env.REACT_APP_LIVE_MARKET_GROUP_ID || "";

const UserLockPage = () => {
  const loggedInUserId = useSelector((state: RootState) => state.user.userData?.user_id ?? "");

  const [clientSearchText, setClientSearchText] = useState("");
  const [clientOptions, setClientOptions] = useState<IClientOption[]>([]);
  const [selectedClient, setSelectedClient] = useState<IClientOption | null>(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [transactionCode, setTransactionCode] = useState("");
  const [sportsTree, setSportsTree] = useState<ISportNode[]>([]);
  const [casinoRows, setCasinoRows] = useState<ICasinoRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  useSyncGlobalPageLoader(isLoading);
  const [loadError, setLoadError] = useState("");
  const [hasLoadedListing, setHasLoadedListing] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({});

  const clientWrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clientSearchRequestIdRef = useRef(0);

  const fetchClients = useCallback(
    async (query: string) => {
      if (!loggedInUserId) {
        return [] as IClientOption[];
      }
      const requestId = ++clientSearchRequestIdRef.current;
      try {
        const { data } = await instance.get("admin/downline-members", {
          params: {
            adminId: loggedInUserId,
            scope: "recursive",
            search: query,
          },
        });
        const rawList: any[] = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.data?.rows)
            ? data.data.rows
            : Array.isArray(data?.data?.members)
              ? data.data.members
              : [];
        const list: IClientOption[] = rawList
          .map((item) => ({
            user_id: item?.user_id ?? item?.admin_id ?? item?.id ?? "",
            user_name: item?.user_name ?? item?.username ?? "",
            display_name: item?.display_name ?? item?.displayName ?? item?.user_name ?? item?.username ?? "",
          }))
          .filter((item) => item.user_id);
        if (requestId !== clientSearchRequestIdRef.current) {
          return [] as IClientOption[];
        }
        return list;
      } catch {
        if (requestId !== clientSearchRequestIdRef.current) {
          return [] as IClientOption[];
        }
        return [] as IClientOption[];
      }
    },
    [loggedInUserId]
  );

  const handleClientSearchChange = (value: string) => {
    setClientSearchText(value);
    if (selectedClient) {
      setSelectedClient(null);
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const q = value.toLowerCase().trim();
      if (q.length < CLIENT_SEARCH_MIN_LENGTH) {
        setClientOptions([]);
        setShowClientDropdown(true);
        return;
      }
      const list = await fetchClients(q);
      setClientOptions(list);
      setShowClientDropdown(true);
    }, 200);
  };

  const handleSelectClient = (client: IClientOption) => {
    setSelectedClient(client);
    setClientSearchText(client.display_name || client.user_name);
    setShowClientDropdown(false);
  };

  const handleReset = () => {
    setClientSearchText("");
    setClientOptions([]);
    setSelectedClient(null);
    setShowClientDropdown(false);
    setTransactionCode("");
    setSportsTree([]);
    setCasinoRows([]);
    setPasswordError("");
    setLoadError("");
    setHasLoadedListing(false);
    setExpandedNodes({});
  };

  const slugify = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  const toggleNodeExpansion = (key: string) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const fetchLockListingData = useCallback(async () => {
    const menuList = await getGameBusinessMenuList(USERLOCK_ORIGIN);
    const sportsMenus = (Array.isArray(menuList) ? menuList : [])
      .filter((item: any) => !item?.is_casino && !isRacing(item?.provider_game_id))
      .map(
        (item: any) =>
          ({
            sportId: String(item?.game_id ?? item?.provider_game_id ?? item?.menu_id ?? ""),
            sportName: String(item?.menu_name || item?.game_name || "-"),
            gameId: String(item?.game_id ?? ""),
            leaguesLoaded: false,
            isLoadingLeagues: false,
          }) as ISportNode
      )
      .filter((item) => item.sportId);

    setSportsTree(sportsMenus);

    const casinoGroups = (Array.isArray(menuList) ? menuList : []).filter(
      (item: any) => item?.is_casino && String(item?.group_id) === LIVE_MARKET_GROUP_ID
    );
    const uniqueGroups = casinoGroups.reduce((acc: { group_id: string; group_name: string }[], item: any) => {
      const groupId = item?.group_id;
      if (!groupId || acc.some((g) => g.group_id === groupId)) return acc;
      acc.push({
        group_id: groupId,
        group_name: item?.group_name || "-",
      });
      return acc;
    }, []);

    const casinoGroupRows = await Promise.all(
      uniqueGroups.map(async (group) => {
        try {
          const events = await liveMarketApi.fetchBusinessGroupGameEvents(group.group_id);
          const activeEvents = Array.isArray(events) ? events.filter((event) => event.is_active) : [];
          return activeEvents.map(
            (event) =>
              ({
                id: `${group.group_id}-${event.game_event_id || event.provider_event_id || event.event_name}`,
                eventName: String(event.event_name || "-"),
                groupName: group.group_name || "-",
              }) as ICasinoRow
          );
        } catch {
          return [] as ICasinoRow[];
        }
      })
    );

    setCasinoRows(casinoGroupRows.flat());
  }, []);

  const loadSportLeagues = useCallback(async (sportId: string, gameId: string) => {
    if (!gameId) return;
    setSportsTree((prev) =>
      prev.map((sport) => (sport.sportId === sportId ? { ...sport, isLoadingLeagues: true } : sport))
    );
    try {
      const leagueRes = await getLeaguesBySportId(gameId);
      const leagues = (Array.isArray(leagueRes) ? leagueRes : [])
        .map(
          (league: any) =>
            ({
              leagueId: String(league?.league_id ?? league?.id ?? ""),
              leagueName: String(league?.league_name ?? league?.name ?? "-"),
              eventsLoaded: false,
              isLoadingEvents: false,
            }) as ILeagueNode
        )
        .filter((league) => league.leagueId);

      setSportsTree((prev) =>
        prev.map((sport) =>
          sport.sportId === sportId
            ? { ...sport, leagues, leaguesLoaded: true, isLoadingLeagues: false }
            : sport
        )
      );
    } catch {
      setSportsTree((prev) =>
        prev.map((sport) =>
          sport.sportId === sportId ? { ...sport, leagues: [], leaguesLoaded: true, isLoadingLeagues: false } : sport
        )
      );
    }
  }, []);

  const loadLeagueEvents = useCallback(async (sportId: string, leagueId: string) => {
    setSportsTree((prev) =>
      prev.map((sport) =>
        sport.sportId === sportId
          ? {
              ...sport,
              leagues: (sport.leagues || []).map((league) =>
                league.leagueId === leagueId ? { ...league, isLoadingEvents: true } : league
              ),
            }
          : sport
      )
    );

    try {
      const eventRes = await getEventsByLeagueId(leagueId);
      const events = (Array.isArray(eventRes) ? eventRes : [])
        .map(
          (event: any) =>
            ({
              eventId: String(event?.game_event_id ?? event?.event_id ?? event?.id ?? ""),
              eventName: String(event?.event_name ?? event?.name ?? "-"),
              marketsLoaded: false,
              isLoadingMarkets: false,
            }) as IEventNode
        )
        .filter((event) => event.eventId);

      setSportsTree((prev) =>
        prev.map((sport) =>
          sport.sportId === sportId
            ? {
                ...sport,
                leagues: (sport.leagues || []).map((league) =>
                  league.leagueId === leagueId
                    ? { ...league, events, eventsLoaded: true, isLoadingEvents: false }
                    : league
                ),
              }
            : sport
        )
      );
    } catch {
      setSportsTree((prev) =>
        prev.map((sport) =>
          sport.sportId === sportId
            ? {
                ...sport,
                leagues: (sport.leagues || []).map((league) =>
                  league.leagueId === leagueId
                    ? { ...league, events: [], eventsLoaded: true, isLoadingEvents: false }
                    : league
                ),
              }
            : sport
        )
      );
    }
  }, []);

  const loadEventMarkets = useCallback(async (sportId: string, leagueId: string, eventId: string) => {
    setSportsTree((prev) =>
      prev.map((sport) =>
        sport.sportId === sportId
          ? {
              ...sport,
              leagues: (sport.leagues || []).map((league) =>
                league.leagueId === leagueId
                  ? {
                      ...league,
                      events: (league.events || []).map((event) =>
                        event.eventId === eventId ? { ...event, isLoadingMarkets: true } : event
                      ),
                    }
                  : league
              ),
            }
          : sport
      )
    );

    try {
      const { data } = await clientInstance.get(`event/market_list/${eventId}`, {
        params: { is_fancy: false },
      });
      const detail = data?.data ?? data ?? {};
      const marketTypeGroups: { key: string; markets: any[] }[] = [
        { key: "match_odd", markets: Array.isArray(detail?.match_odd) ? detail.match_odd : [] },
        { key: "bookmaker", markets: Array.isArray(detail?.bookmaker) ? detail.bookmaker : [] },
        { key: "fancy", markets: Array.isArray(detail?.fancy) ? detail.fancy : [] },
      ];
      const marketTypes: IMarketTypeNode[] = marketTypeGroups
        .filter((group) => group.markets.length > 0)
        .map((group) => ({
          marketType: group.key,
          markets: group.markets.map((market) => ({
            marketId: String(market?.market_id ?? market?.provider_market_id ?? market?.id ?? ""),
            marketName: String(market?.market_name ?? market?.runner_name ?? "-"),
          })),
        }));

      setSportsTree((prev) =>
        prev.map((sport) =>
          sport.sportId === sportId
            ? {
                ...sport,
                leagues: (sport.leagues || []).map((league) =>
                  league.leagueId === leagueId
                    ? {
                        ...league,
                        events: (league.events || []).map((event) =>
                          event.eventId === eventId
                            ? { ...event, marketTypes, marketsLoaded: true, isLoadingMarkets: false }
                            : event
                        ),
                      }
                    : league
                ),
              }
            : sport
        )
      );
    } catch {
      setSportsTree((prev) =>
        prev.map((sport) =>
          sport.sportId === sportId
            ? {
                ...sport,
                leagues: (sport.leagues || []).map((league) =>
                  league.leagueId === leagueId
                    ? {
                        ...league,
                        events: (league.events || []).map((event) =>
                          event.eventId === eventId
                            ? { ...event, marketTypes: [], marketsLoaded: true, isLoadingMarkets: false }
                            : event
                        ),
                      }
                    : league
                ),
              }
            : sport
        )
      );
    }
  }, []);

  const handleLoad = async (e: FormEvent) => {
    e.preventDefault();
    const password = transactionCode.trim();
    if (!password) {
      setPasswordError("Transaction Code is required");
      return;
    }

    setIsLoading(true);
    setPasswordError("");
    setLoadError("");
    setSportsTree([]);
    setCasinoRows([]);
    setHasLoadedListing(false);

    try {
      const { data } = await instance.post("admin/verify-transaction-password", {
        transaction_password: password,
      });
      const isValid = Boolean(data?.data?.is_valid ?? data?.is_valid);
      if (!isValid) {
        setPasswordError("Invalid transaction password.");
        return;
      }
      await fetchLockListingData();
      setHasLoadedListing(true);
    } catch (error: any) {
      const message =
        error?.response?.data?.status_message || error?.response?.data?.message || "Unable to load lock listing.";
      setLoadError(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (clientWrapperRef.current && !clientWrapperRef.current.contains(e.target as Node)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div className="listing-grid report-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-0 font-size-18">General Lock</h4>
            <div className="page-title-right" />
          </div>
        </div>
      </div>

      <div className="user-lock-container">
        <div className="m-t-20">
          <div>
            <form method="post" className="ajaxFormSubmit" onSubmit={handleLoad}>
              <div className="row row5">
                <div className="col-md-3">
                  <div className="form-group user-lock-search" style={{ position: "relative" }} ref={clientWrapperRef}>
                    <label htmlFor="user-lock-client-search">Search By Client Name</label>
                    <input
                      id="user-lock-client-search"
                      type="text"
                      autoComplete="off"
                      spellCheck={false}
                      placeholder="Select option"
                      className="form-control"
                      value={clientSearchText}
                      onChange={(e) => handleClientSearchChange(e.target.value)}
                      onFocus={() => {
                        setClientOptions([]);
                        clientSearchRequestIdRef.current += 1;
                        setShowClientDropdown(true);
                      }}
                    />
                    {showClientDropdown && (
                      <ul
                        className="list-group"
                        style={{
                          position: "absolute",
                          zIndex: 1000,
                          width: "100%",
                          maxHeight: "300px",
                          overflowY: "auto",
                          boxShadow: "0 2px 8px rgba(0,0,0,.15)",
                        }}
                      >
                        {clientOptions.length === 0 ? (
                          <li className="list-group-item">
                            {clientSearchText.trim().length >= CLIENT_SEARCH_MIN_LENGTH
                              ? "No elements found. Consider changing the search query."
                              : "List is empty."}
                          </li>
                        ) : (
                          clientOptions.map((client) => (
                            <li key={client.user_id} className="list-group-item list-group-item-action">
                              <button
                                type="button"
                                className="btn btn-link p-0 text-left w-100"
                                style={{ textDecoration: "none", color: "inherit" }}
                                onClick={() => handleSelectClient(client)}
                              >
                                {client.display_name || client.user_name}
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="col-md-2">
                  <div className="form-group">
                    <label htmlFor="user-lock-transaction-code">Transaction Code</label>
                    <input
                      id="user-lock-transaction-code"
                      type="password"
                      placeholder="Transaction Code"
                      className="form-control d-inline-block"
                      value={transactionCode}
                      onChange={(e) => setTransactionCode(e.target.value)}
                    />
                  </div>
                </div>

                <div className="col-md-2">
                  <div aria-hidden="true" style={{ width: "100%" }}>
                    &nbsp;
                  </div>
                  <button type="submit" id="loaddata" className="btn btn-primary" disabled={isLoading}>
                    {isLoading ? "Loading..." : "Load"}
                  </button>{" "}
                  <button type="button" id="reset" className="btn btn-light" onClick={handleReset}>
                    Reset
                  </button>
                </div>
              </div>
            </form>
          </div>

          {passwordError ? <div className="alert alert-warning mt-2">{passwordError}</div> : null}
          {loadError ? <div className="alert alert-danger mt-2">{loadError}</div> : null}

          {hasLoadedListing && (
            <div className="row mt-3" aria-busy={isLoading}>
              <div className="col-lg-6 col-12">
                <h4 className="ptitle">Events</h4>
                <ul id="accordian1" className="navbar-nav user-lock-nav">
                  {sportsTree.length === 0 ? (
                    <li className="nav-item dropdown show">
                      <span className="custom-control custom-checkbox">
                        <input type="checkbox" id="sports-empty" className="custom-control-input" disabled />
                        <label htmlFor="sports-empty" className="custom-control-label">
                          There are no records to show
                        </label>
                      </span>
                    </li>
                  ) : (
                    sportsTree.map((sport) => {
                      const sportKey = slugify(`${sport.sportId}-${sport.sportName}`) || "sport";
                      const sportNodeKey = `sport-${sportKey}`;
                      const parentId = `sport-checkbox-${sportKey}`;
                      const submenuId = `menu-sport-${sportKey}`;
                      const isSportExpanded = Boolean(expandedNodes[sportNodeKey]);
                      return (
                        <li key={parentId} className="nav-item dropdown show">
                          <button
                            type="button"
                            title="click here to expand"
                            className="arrow-icon btn btn-link p-0"
                            aria-expanded={isSportExpanded}
                            onClick={() => {
                              const willExpand = !isSportExpanded;
                              toggleNodeExpansion(sportNodeKey);
                              if (willExpand && !sport.leaguesLoaded && !sport.isLoadingLeagues) {
                                loadSportLeagues(sport.sportId, sport.gameId);
                              }
                            }}
                          >
                            <i className={`fas ${isSportExpanded ? "fa-angle-down" : "fa-angle-right"}`} />
                          </button>
                          <span className="custom-control custom-checkbox">
                            <input type="checkbox" id={parentId} className="custom-control-input" value={sport.sportId} />
                            <label htmlFor={parentId} className="custom-control-label">
                              {sport.sportName}
                            </label>
                          </span>
                          <ul
                            id={submenuId}
                            data-parent="#accordian1"
                            className={`sub-menu collapse ${isSportExpanded ? "show" : ""}`}
                          >
                            {sport.isLoadingLeagues ? (
                              <li>
                                <span className="custom-control custom-checkbox">
                                  <input type="checkbox" id={`loading-league-${sportKey}`} className="custom-control-input" disabled />
                                  <label htmlFor={`loading-league-${sportKey}`} className="custom-control-label">
                                    Loading leagues...
                                  </label>
                                </span>
                              </li>
                            ) : (sport.leagues || []).length === 0 ? (
                              <li>
                                <span className="custom-control custom-checkbox">
                                  <input type="checkbox" id={`empty-league-${sportKey}`} className="custom-control-input" disabled />
                                  <label htmlFor={`empty-league-${sportKey}`} className="custom-control-label">
                                    No leagues available
                                  </label>
                                </span>
                              </li>
                            ) : (
                              (sport.leagues || []).map((league) => {
                              const leagueKey = slugify(`${league.leagueId}-${league.leagueName}`) || "league";
                              const leagueNodeKey = `league-${sportKey}-${leagueKey}`;
                              const leagueCheckboxId = `league-checkbox-${sportKey}-${leagueKey}`;
                              const leagueSubmenuId = `menu-league-${sportKey}-${leagueKey}`;
                              const isLeagueExpanded = Boolean(expandedNodes[leagueNodeKey]);

                              return (
                                <li key={leagueCheckboxId}>
                                  <button
                                    type="button"
                                    title="click here to expand"
                                    className="arrow-icon btn btn-link p-0"
                                    aria-expanded={isLeagueExpanded}
                                    onClick={() => {
                                      const willExpand = !isLeagueExpanded;
                                      toggleNodeExpansion(leagueNodeKey);
                                      if (willExpand && !league.eventsLoaded && !league.isLoadingEvents) {
                                        loadLeagueEvents(sport.sportId, league.leagueId);
                                      }
                                    }}
                                  >
                                    <i className={`fas ${isLeagueExpanded ? "fa-angle-down" : "fa-angle-right"}`} />
                                  </button>
                                  <span className="custom-control custom-checkbox">
                                    <input
                                      type="checkbox"
                                      id={leagueCheckboxId}
                                      className="custom-control-input"
                                      value={league.leagueId}
                                    />
                                    <label htmlFor={leagueCheckboxId} className="custom-control-label">
                                      {league.leagueName}
                                    </label>
                                  </span>
                                  <ul
                                    id={leagueSubmenuId}
                                    data-parent={`#${submenuId}`}
                                    className={`sub-menu collapse ${isLeagueExpanded ? "show" : ""}`}
                                  >
                                    {league.isLoadingEvents ? (
                                      <li>
                                        <span className="custom-control custom-checkbox">
                                          <input
                                            type="checkbox"
                                            id={`loading-event-${sportKey}-${leagueKey}`}
                                            className="custom-control-input"
                                            disabled
                                          />
                                          <label
                                            htmlFor={`loading-event-${sportKey}-${leagueKey}`}
                                            className="custom-control-label"
                                          >
                                            Loading events...
                                          </label>
                                        </span>
                                      </li>
                                    ) : (league.events || []).length === 0 ? (
                                      <li>
                                        <span className="custom-control custom-checkbox">
                                          <input
                                            type="checkbox"
                                            id={`empty-event-${sportKey}-${leagueKey}`}
                                            className="custom-control-input"
                                            disabled
                                          />
                                          <label
                                            htmlFor={`empty-event-${sportKey}-${leagueKey}`}
                                            className="custom-control-label"
                                          >
                                            No events available
                                          </label>
                                        </span>
                                      </li>
                                    ) : (
                                      (league.events || []).map((event) => {
                                      const eventKey = slugify(`${event.eventId}-${event.eventName}`) || "event";
                                      const eventNodeKey = `event-${sportKey}-${leagueKey}-${eventKey}`;
                                      const eventCheckboxId = `event-checkbox-${sportKey}-${leagueKey}-${eventKey}`;
                                      const eventSubmenuId = `menu-event-${sportKey}-${leagueKey}-${eventKey}`;
                                      const isEventExpanded = Boolean(expandedNodes[eventNodeKey]);

                                      return (
                                        <li key={eventCheckboxId}>
                                          <button
                                            type="button"
                                            title="click here to expand"
                                            className="arrow-icon btn btn-link p-0"
                                            aria-expanded={isEventExpanded}
                                            onClick={() => {
                                              const willExpand = !isEventExpanded;
                                              toggleNodeExpansion(eventNodeKey);
                                              if (willExpand && !event.marketsLoaded && !event.isLoadingMarkets) {
                                                loadEventMarkets(sport.sportId, league.leagueId, event.eventId);
                                              }
                                            }}
                                          >
                                            <i className={`fas ${isEventExpanded ? "fa-angle-down" : "fa-angle-right"}`} />
                                          </button>
                                          <span className="custom-control custom-checkbox">
                                            <input
                                              type="checkbox"
                                              id={eventCheckboxId}
                                              className="custom-control-input"
                                              value={event.eventId}
                                            />
                                            <label htmlFor={eventCheckboxId} className="custom-control-label">
                                              {event.eventName}
                                            </label>
                                          </span>
                                          <ul
                                            id={eventSubmenuId}
                                            data-parent={`#${leagueSubmenuId}`}
                                            className={`sub-menu collapse ${isEventExpanded ? "show" : ""}`}
                                          >
                                            {event.isLoadingMarkets ? (
                                              <li>
                                                <span className="custom-control custom-checkbox">
                                                  <input
                                                    type="checkbox"
                                                    id={`loading-market-${sportKey}-${leagueKey}-${eventKey}`}
                                                    className="custom-control-input"
                                                    disabled
                                                  />
                                                  <label
                                                    htmlFor={`loading-market-${sportKey}-${leagueKey}-${eventKey}`}
                                                    className="custom-control-label"
                                                  >
                                                    Loading markets...
                                                  </label>
                                                </span>
                                              </li>
                                            ) : (event.marketTypes || []).length === 0 ? (
                                              <li>
                                                <span className="custom-control custom-checkbox">
                                                  <input
                                                    type="checkbox"
                                                    id={`empty-market-${sportKey}-${leagueKey}-${eventKey}`}
                                                    className="custom-control-input"
                                                    disabled
                                                  />
                                                  <label
                                                    htmlFor={`empty-market-${sportKey}-${leagueKey}-${eventKey}`}
                                                    className="custom-control-label"
                                                  >
                                                    No markets available
                                                  </label>
                                                </span>
                                              </li>
                                            ) : (
                                              (event.marketTypes || []).map((marketTypeNode) => (
                                              <li key={`${eventCheckboxId}-${marketTypeNode.marketType}`}>
                                                <h5 className="mb-0 font-size-16">{marketTypeNode.marketType}</h5>
                                                <ul className="sub-menu collapse show">
                                                  {marketTypeNode.markets.map((market) => {
                                                    const marketKey = slugify(
                                                      `${event.eventId}-${marketTypeNode.marketType}-${market.marketId}-${market.marketName}`
                                                    );
                                                    const marketCheckboxId = `market-checkbox-${marketKey || "market"}`;
                                                    return (
                                                      <li key={marketCheckboxId}>
                                                        <span className="custom-control custom-checkbox">
                                                          <input
                                                            type="checkbox"
                                                            id={marketCheckboxId}
                                                            className="custom-control-input"
                                                            value={market.marketId}
                                                          />
                                                          <label htmlFor={marketCheckboxId} className="custom-control-label">
                                                            {market.marketName}
                                                          </label>
                                                        </span>
                                                      </li>
                                                    );
                                                  })}
                                                </ul>
                                              </li>
                                              ))
                                            )}
                                          </ul>
                                        </li>
                                      );
                                      })
                                    )}
                                  </ul>
                                </li>
                              );
                              })
                            )}
                          </ul>
                        </li>
                      );
                    })
                  )}
                </ul>
              </div>

              <div className="col-lg-6 col-12">
                <h4 className="ptitle">Casino List</h4>
                <ul className="user-lock-nav">
                  {casinoRows.length === 0 ? (
                    <li>
                      <span className="custom-control custom-checkbox">
                        <input type="checkbox" id="casino-empty" className="custom-control-input" disabled />
                        <label htmlFor="casino-empty" className="custom-control-label">
                          There are no records to show
                        </label>
                      </span>
                    </li>
                  ) : (
                    casinoRows.map((row) => {
                      const casinoKey = slugify(row.id || row.eventName) || "casino";
                      const casinoId = `casino-${casinoKey}`;
                      return (
                        <li key={row.id}>
                          <span className="custom-control custom-checkbox">
                            <input type="checkbox" id={casinoId} className="custom-control-input" value={row.id} />
                            <label htmlFor={casinoId} className="custom-control-label">
                              {row.eventName || "-"}
                            </label>
                          </span>
                        </li>
                      );
                    })
                  )}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserLockPage;
